import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Area, AreaKind, Road, World } from '../../shared/types.js';
import { bboxOfPoly, distToSegment2, pointInPoly, polyArea, resample } from '../../shared/geom.js';
import { hashUnit } from '../../shared/rng.js';
import { ROAD_SPECS } from '../../shared/osm/classify.js';
import { BuildingIndex, SurfaceIndex } from '../../shared/collision.js';
import { Heightfield } from '../../shared/heightfield.js';
import type { JunctionIndex } from './roadLines.js';
import type { JunctionNode } from './junctionArms.js';

/**
 * Trees and street lamps, scattered from the polygons and centrelines the world already
 * has.
 *
 * The OSM data we fetch is ways and relations only — no individual nodes — so there are no
 * real tree or lamppost positions to place. What there *is* is a polygon that says "this is
 * a park" and a centreline that says "this is a main road", and that is enough to put props
 * somewhere defensible: trees inside green areas, lamps at the kerb of the wider streets.
 * Nothing is placed at random in the abstract; every prop is derived from a piece of real
 * map data.
 *
 * Two rules make this safe to ship:
 *
 * - **Deterministic.** Positions come from `hashUnit(seed, id)` keyed on the area or road
 *   id and the instance index, exactly like building colours, so the same region and seed
 *   give the same forest on every machine — and adding one park never moves the trees in
 *   the next one.
 * - **Instanced.** Two thousand trees are two draw calls. Drawn individually they would be
 *   two thousand, which is the mistake merging the city was meant to avoid.
 */

export interface PropMeshes {
  objects: THREE.Object3D[];
  triangles: number;
  treeCount: number;
  lampCount: number;
  /**
   * Where each lamp's fitting is, for the street-light map — see `streetLight.ts`. `scale` is
   * the lamp's own: 1 for a street lamp, `PARK_LAMP_SCALE` for a lantern on a park path.
   */
  lampHeads: { x: number; z: number; scale: number }[];
  /**
   * The materials of everything here that stands in the street light — the trees, the
   * columns — for `streetLight.ts` to patch, as instanced meshes (§93).
   */
  litMaterials: THREE.Material[];
  signalCount: number;
  /**
   * 0 is full day, 1 is full night. Turns on the pool of light under each lamp and the glow
   * at its head — see the comment above `makeLampPoolGeometry`. Driven from `WorldMeshes`,
   * which is driven from the weather.
   */
  setNight(amount: number): void;
  dispose(): void;
}

export interface PropOptions {
  /** Hard ceiling on trees, so a region that is mostly forest cannot eat the budget. */
  maxTrees?: number;
  maxLamps?: number;
  maxSignals?: number;
}

/** Square metres of green per tree, by what the polygon is. */
const TREE_SPACING: Partial<Record<AreaKind, number>> = {
  wood: 120,
  park: 250,
  grass: 480,
  // Sports grounds get a tree line round the edge and nothing in the middle — see below.
  pitch: 900,
};

/** No single polygon may contribute more than this, however big it is. */
const MAX_TREES_PER_AREA = 320;

/** Clearance a tree needs from tarmac and from a wall, in metres. */
const ROAD_CLEARANCE = 2.6;
const BUILDING_CLEARANCE = 2.4;

/** Lamps go on roads at least this wide, this far apart, alternating sides. */
export const LAMP_MIN_ROAD_WIDTH = 9.5;
const LAMP_SPACING = 28;

export function buildProps(
  world: World,
  junctions: JunctionIndex,
  terrain: Heightfield = Heightfield.flat(),
  opts: PropOptions = {},
  nodes: JunctionNode[] = [],
  surfaceOf: (road: Road, x: number, z: number) => number = (_road, x, z) => terrain.heightAt(x, z),
): PropMeshes {
  const maxTrees = opts.maxTrees ?? 4200;
  const maxLamps = opts.maxLamps ?? 1600;
  const maxSignals = opts.maxSignals ?? 600;

  const surface = new SurfaceIndex(world);
  const buildings = new BuildingIndex(world);

  const broadleaf: Placement[] = [];
  const conifer: Placement[] = [];
  scatterTrees(world, surface, buildings, broadleaf, conifer, maxTrees);

  const lamps: Placement[] = [];
  // The lamps somebody mapped first, so the guessed ones can stand aside for them.
  placeMappedLamps(world, surface, buildings, terrain, lamps, maxLamps);
  const mapped = lamps.length;
  placeLamps(world, junctions, surface, buildings, lamps, maxLamps, surfaceOf);
  placeParkLamps(world, surface, buildings, terrain, lamps, Math.min(maxLamps, lamps.length + MAX_PARK_LAMPS), mapped);

  const signals: Placement[] = [];
  placeSignals(nodes, surface, buildings, signals, maxSignals, surfaceOf);

  // Placement is a 2D problem — what is a park, what is tarmac, what is a wall — so the
  // terrain is applied once at the end rather than being threaded through it. A tree stands
  // where the ground is, and on a flat world that is exactly Y = 0, which is what it always
  // was. Lamps and signals are the exception, because they belong to a street and a street
  // can be on a bridge: they are stood on that street's own surface as they are placed.
  for (const p of broadleaf) p.y = terrain.heightAt(p.x, p.z);
  for (const p of conifer) p.y = terrain.heightAt(p.x, p.z);

  const objects: THREE.Object3D[] = [];
  const disposables: { dispose(): void }[] = [];
  let triangles = 0;

  const foliage = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
  disposables.push(foliage);

  for (const [name, geometry, placements] of [
    ['trees-broadleaf', makeBroadleafGeometry(), broadleaf],
    ['trees-conifer', makeConiferGeometry(), conifer],
  ] as const) {
    const mesh = toInstancedMesh(name, geometry, foliage, placements, true);
    if (mesh) {
      objects.push(mesh);
      disposables.push(mesh, geometry);
      triangles += triangleCount(geometry) * placements.length;
    } else {
      geometry.dispose();
    }
  }

  // Painted steel, shared by the lamp columns and the traffic signals — they are the same
  // material in the world and a second Lambert with identical settings would only be a second
  // draw call. Owned by `disposables` from here rather than by whichever mesh happens to be
  // built, because either of them can come out empty: a region with signals but no wide
  // enough streets for lamps would otherwise be handed an already-disposed material.
  const poleMaterial = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
  disposables.push(poleMaterial);

  const lampGeometry = makeLampGeometry();
  // A 10 cm pole is thinner than a shadow-map texel, so it would only ever contribute
  // flickering. It still receives the sun, which is what makes it read as three-dimensional.
  const lampMesh = toInstancedMesh('street-lamps', lampGeometry, poleMaterial, lamps, false);
  if (lampMesh) {
    objects.push(lampMesh);
    disposables.push(lampMesh, lampGeometry);
    triangles += triangleCount(lampGeometry) * lamps.length;
  } else {
    lampGeometry.dispose();
  }

  const signalGeometry = makeSignalGeometry();
  const signalMesh = toInstancedMesh('traffic-signals', signalGeometry, poleMaterial, signals, false);
  if (signalMesh) {
    objects.push(signalMesh);
    disposables.push(signalMesh, signalGeometry);
    triangles += triangleCount(signalGeometry) * signals.length;
  } else {
    signalGeometry.dispose();
  }

  // --- night lighting: a pool on the ground and a glow at the head -------------------
  //
  // A `PointLight` per lamp is off the table — a region has hundreds of them, and three.js
  // recomputes lighting for every one on every fragment it touches. Both of these fake it
  // with unlit, additive geometry instead: the pool is a few triangles of vertex-coloured
  // fan, the glow is a small blob at the head, and neither costs anything a real light would
  // — no shadow map, no per-pixel lighting pass, one extra draw call each for the whole city.
  //
  // Both meshes are built and instanced exactly like the lamp posts themselves, sharing the
  // same `lamps` placements, so a glow always lands on the lamp it belongs to for free. They
  // start `visible = false`: the day render must be pixel-identical to what it was before
  // this existed, and a material at zero opacity is *nearly* zero contribution under additive
  // blending but a hidden mesh is exactly zero, which is the guarantee actually worth having.
  //
  // The pool is the one of the two that cannot be instanced. An instance is one rigid
  // transform of one geometry, so a flat disc stays flat — and the ground it is lying on is
  // not. Sitting at a fixed height above the *lamp's* base, the far side of a three-metre
  // pool went underground on any street with a camber or a slope worth the name, and the
  // depth test quietly removed the half of it that mattered. Reported as "some lamps don't
  // show their light on the road", which is exactly what it was.
  //
  // So the pools are baked into one merged mesh with every vertex dropped onto the terrain
  // individually. It costs one buffer instead of one matrix per lamp — about 700 KB at the
  // 1600-lamp cap — and is still a single draw call, the same as the instanced version.
  const poolGeometry = buildLampPools(lamps, terrain);
  const poolMaterial = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
  });
  const poolMesh = poolGeometry ? new THREE.Mesh(poolGeometry, poolMaterial) : null;
  if (poolMesh && poolGeometry) {
    poolMesh.name = 'lamp-pools';
    poolMesh.visible = false;
    poolMesh.matrixAutoUpdate = false;
    objects.push(poolMesh);
    disposables.push(poolGeometry, poolMaterial);
    triangles += POOL_SEGMENTS * lamps.length;
  } else {
    poolMaterial.dispose();
  }

  const glowGeometry = makeLampGlowGeometry();
  const glowMaterial = new THREE.MeshBasicMaterial({
    // Several times white: the head is the brightest thing in a night street, and the bloom
    // only catches what is above 1 (§80). At 1 it was a dull orange bead.
    color: LAMP_LIGHT_COLOR.clone().multiplyScalar(GLOW_INTENSITY),
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
    side: THREE.DoubleSide,
  });
  const glowMesh = toInstancedMesh('lamp-glows', glowGeometry, glowMaterial, lamps, false);
  if (glowMesh) {
    glowMesh.visible = false;
    objects.push(glowMesh);
    disposables.push(glowMesh, glowGeometry, glowMaterial);
    triangles += triangleCount(glowGeometry) * lamps.length;
  } else {
    glowGeometry.dispose();
    glowMaterial.dispose();
  }

  return {
    objects,
    triangles,
    treeCount: broadleaf.length + conifer.length,
    lampCount: lamps.length,
    // `POOL_OFFSET_X` along the arm, by the same transform the pools are placed with.
    lampHeads: lamps.map((lamp) => ({
      x: lamp.x + lamp.scale * POOL_OFFSET_X * Math.cos(lamp.rotY),
      z: lamp.z - lamp.scale * POOL_OFFSET_X * Math.sin(lamp.rotY),
      scale: lamp.scale,
    })),
    litMaterials: [foliage, poleMaterial],
    signalCount: signals.length,
    setNight(amount: number) {
      const night = amount < 0 ? 0 : amount > 1 ? 1 : amount;
      if (poolMesh) {
        poolMesh.visible = night > 0;
        poolMaterial.opacity = night * POOL_MAX_OPACITY;
      }
      if (glowMesh) {
        glowMesh.visible = night > 0;
        glowMaterial.opacity = night * GLOW_MAX_OPACITY;
      }
    },
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// placement
// ---------------------------------------------------------------------------

interface Placement {
  x: number;
  z: number;
  /** Filled in once placement is done: the height of the ground under (x, z). */
  y: number;
  rotY: number;
  scale: number;
  /** Per-instance tint, kept close to 1 so it varies the green without recolouring it. */
  tint: THREE.Color;
}

/**
 * A stable number in [0,1) for (seed, element id, draw index).
 *
 * `hashUnit` gives one value per id; folding the draw index into the seed rather than into
 * the id keeps every element's sequence independent, so a park with 200 trees and the park
 * next to it never share a run of positions.
 */
function draw(seed: number, id: number, k: number): number {
  return hashUnit(seed ^ Math.imul(k + 1, 0x9e3779b1), id);
}

function scatterTrees(
  world: World,
  surface: SurfaceIndex,
  buildings: BuildingIndex,
  broadleaf: Placement[],
  conifer: Placement[],
  maxTrees: number,
): void {
  const seed = world.seed;
  let placed = 0;
  const courts = world.areas.filter((a) => a.sport);

  for (const area of world.areas) {
    const spacing = TREE_SPACING[area.kind];
    if (spacing === undefined) continue;
    // A pitch marked out for its sport is a court with lines and a fence (`pitches.ts`), and
    // a tree in the goalmouth is the one thing on it that cannot be there — nor one from the
    // park the pitch is in.
    if (area.sport) continue;
    if (placed >= maxTrees) break;

    let net = Math.abs(polyArea(area.poly));
    for (const hole of area.holes) net -= Math.abs(polyArea(hole));
    const target = Math.min(MAX_TREES_PER_AREA, Math.round(net / spacing), maxTrees - placed);
    if (target < 1) continue;

    const bb = bboxOfPoly(area.poly);
    const spanX = bb.maxX - bb.minX;
    const spanZ = bb.maxZ - bb.minZ;
    if (spanX < 4 || spanZ < 4) continue;

    // A pitch is a football field or a running track: trees belong round the edge of one,
    // not standing on it. Everything else wants them comfortably inside the boundary.
    const onPitch = area.kind === 'pitch';
    const minEdge = onPitch ? 2 : 3.5;
    const maxEdge = onPitch ? 9 : Infinity;

    let made = 0;
    const attempts = target * 4 + 10;
    for (let i = 0; i < attempts && made < target; i++) {
      const x = bb.minX + draw(seed, area.id, i * 3) * spanX;
      const z = bb.minZ + draw(seed, area.id, i * 3 + 1) * spanZ;
      const edge = edgeDistance(area, x, z);
      if (edge < minEdge || edge > maxEdge) continue;
      if (nearRoad(surface, x, z)) continue;
      if (buildings.resolveCircle(x, z, BUILDING_CLEARANCE)) continue;
      if (courts.some((c) => pointInPoly(c.poly, x, z))) continue;

      const v = draw(seed, area.id, i * 3 + 2);
      const isConifer = area.kind === 'wood' ? v < 0.8 : v < 0.18;
      (isConifer ? conifer : broadleaf).push({
        x,
        z,
        y: 0,
        rotY: v * Math.PI * 2,
        scale: 0.74 + ((v * 9.71) % 1) * 0.66,
        tint: tintFor(v),
      });
      made++;
      placed++;
    }
  }
}

/**
 * Distance from a point to the nearest boundary of the area — outer ring or courtyard.
 *
 * Returns -1 for points outside, which folds the containment test and the margin test into
 * one pass over the rings.
 */
function edgeDistance(area: Area, x: number, z: number): number {
  if (!pointInPoly(area.poly, x, z)) return -1;
  for (const hole of area.holes) if (pointInPoly(hole, x, z)) return -1;
  let best = Infinity;
  best = Math.min(best, ringDistance(area.poly, x, z));
  for (const hole of area.holes) best = Math.min(best, ringDistance(hole, x, z));
  return best;
}

function ringDistance(ring: number[], x: number, z: number): number {
  let best = Infinity;
  const n = ring.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const { d2 } = distToSegment2(x, z, ring[j * 2], ring[j * 2 + 1], ring[i * 2], ring[i * 2 + 1]);
    if (d2 < best) best = d2;
  }
  return Math.sqrt(best);
}

/**
 * Rejects anything a car could drive over.
 *
 * `SurfaceIndex` deliberately reports a road slightly narrower than the ribbon that is
 * drawn, so testing the trunk position alone would leave trees standing on the paint.
 * Sampling a ring around it pushes them properly clear of the kerb.
 */
function nearRoad(surface: SurfaceIndex, x: number, z: number): boolean {
  if (surface.sample(x, z).onRoad) return true;
  for (const [dx, dz] of OFFSETS) {
    if (surface.sample(x + dx * ROAD_CLEARANCE, z + dz * ROAD_CLEARANCE).onRoad) return true;
  }
  return false;
}

const OFFSETS: [number, number][] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [0.7, 0.7],
  [-0.7, -0.7],
];

function tintFor(v: number): THREE.Color {
  const a = (v * 13.37) % 1;
  const b = (v * 71.3) % 1;
  return new THREE.Color(0.9 + a * 0.16, 0.86 + b * 0.26, 0.86 + a * 0.18);
}

/**
 * Lamps every `LAMP_SPACING` metres along the wider roads, offset to the kerb and
 * alternating sides — which is how a real street is lit, and half the poles of doing both
 * sides.
 */
function placeLamps(
  world: World,
  junctions: JunctionIndex,
  surface: SurfaceIndex,
  buildings: BuildingIndex,
  out: Placement[],
  maxLamps: number,
  surfaceOf: (road: Road, x: number, z: number) => number,
): void {
  const seed = world.seed;

  for (const road of world.roads) {
    if (out.length >= maxLamps) break;
    if (!ROAD_SPECS[road.cls].drivable || road.width < LAMP_MIN_ROAD_WIDTH) continue;
    const offset = road.width / 2 + 1.1;
    // Which side the first lamp goes on, per road, so a crossroads of two lit streets does
    // not end up with four poles in a neat square.
    const startSide = hashUnit(seed, road.id) < 0.5 ? 0 : 1;

    for (const run of junctions.clear(road.pts, 3, LAMP_SPACING * 0.5)) {
      // Resampling first means the walk below steps in even metres whatever the original
      // vertex spacing was.
      const line = resample(run, LAMP_SPACING);
      let index = startSide;
      for (let i = 1; i < line.length / 2 - 1; i++) {
        if (out.length >= maxLamps) break;
        const x = line[i * 2];
        const z = line[i * 2 + 1];
        const dx = line[(i + 1) * 2] - line[(i - 1) * 2];
        const dz = line[(i + 1) * 2 + 1] - line[(i - 1) * 2 + 1];
        const len = Math.hypot(dx, dz);
        if (len < 1e-3) continue;
        // Left of travel on even indices, right on odd.
        const side = index++ % 2 === 0 ? 1 : -1;
        const nx = (-dz / len) * side;
        const nz = (dx / len) * side;
        const px = x + nx * offset;
        const pz = z + nz * offset;
        if (buildings.resolveCircle(px, pz, 0.6)) continue;
        if (lampWithin(out, px, pz, MAPPED_LAMP_CLEARANCE)) continue;
        // The offset is measured from *this* road's centreline, which says nothing about
        // what else is there. Where two drivable ways run close together — a slip road
        // beside an avenue, an oblique crossing that shares no OSM node so gets no junction
        // disc — the kerb of one lands in the carriageway of the other. Manhattan put 8
        // poles in the road before this test existed.
        if (surface.sample(px, pz).onRoad) continue;
        out.push({
          x: px,
          z: pz,
          y: surfaceOf(road, px, pz),
          // Local +X is the arm; point it back over the carriageway.
          rotY: Math.atan2(nz, -nx),
          scale: 1,
          tint: WHITE,
        });
      }
    }
  }
}

/** How close a guessed lamp may stand to one somebody mapped. Half the posts' spacing. */
const MAPPED_LAMP_CLEARANCE = 14;

/**
 * A park lantern is the street lamp at this size: a 4 m column with a short bracket, which is
 * what a park in a Polish town is lit by. The same instanced mesh, so no new draw call.
 */
export const PARK_LAMP_SCALE = 0.55;
/** Lanterns along a park path, metres apart, alternating sides, and the most a region gets. */
const PARK_LAMP_SPACING = 20;
const PARK_LAMP_OFFSET = 1.5;
const MAX_PARK_LAMPS = 500;
/** No lantern this close to another lamp of any kind, so two paths meeting do not double up. */
const PARK_LAMP_CLEARANCE = 10;

/** Whether any lamp in `lamps` stands within `r` of a point. Linear, and called a few thousand times. */
function lampWithin(lamps: Placement[], x: number, z: number, r: number, from = 0, to = lamps.length): boolean {
  const r2 = r * r;
  for (let i = from; i < to; i++) {
    const dx = lamps[i].x - x;
    const dz = lamps[i].z - z;
    if (dx * dx + dz * dz < r2) return true;
  }
  return false;
}

/**
 * The lamps OSM has (`highway=street_lamp`), where it has them. Rare — thirteen in the first
 * Leszno stop — but each is a real lamp in a real place, which no rule can be. One on a
 * pavement is a street lamp with its arm out over the road; anywhere else it is a lantern.
 */
function placeMappedLamps(
  world: World,
  surface: SurfaceIndex,
  buildings: BuildingIndex,
  terrain: Heightfield,
  out: Placement[],
  maxLamps: number,
): void {
  const pts = world.lamps ?? [];
  for (let i = 0; i < pts.length && out.length < maxLamps; i += 2) {
    const x = pts[i];
    const z = pts[i + 1];
    if (buildings.resolveCircle(x, z, 0.3)) continue;
    const under = surface.sample(x, z);
    // Mapped in the carriageway is mapped a metre or two off; it does not stand in the road.
    if (under.onRoad) continue;
    const street = under.pavement && (under.nx !== 0 || under.nz !== 0);
    out.push({
      x,
      z,
      y: terrain.heightAt(x, z),
      // As in `placeLamps`: local +X is the arm, pointed back over the carriageway.
      rotY: street ? Math.atan2(under.nz, -under.nx) : hashUnit(world.seed, i + 1) * Math.PI * 2,
      scale: street ? 1 : PARK_LAMP_SCALE,
      tint: WHITE,
    });
  }
}

/**
 * Lanterns along the paths through a park.
 *
 * §87 lit named footways and left unnamed ones dark, on the grounds that an unnamed footway
 * is a path across a park and a path across a park is not lit. In a Polish town it is — the
 * park off the market square is somewhere people walk home through — and the park was the one
 * black hole in a lamplit city: Plac Kościuszki in Leszno at night was a dark green rectangle
 * with the street lamps round its edge. So a path inside a park gets lanterns, visible ones,
 * because in a park the lamp is part of the picture in a way a lantern on a wall is not.
 * Paths outside parks — across fields, along a railway — stay dark.
 */
function placeParkLamps(
  world: World,
  surface: SurfaceIndex,
  buildings: BuildingIndex,
  terrain: Heightfield,
  out: Placement[],
  maxLamps: number,
  mapped: number,
): void {
  const parks = world.areas.filter((a) => a.kind === 'park').map((a) => ({ area: a, bb: bboxOfPoly(a.poly) }));
  if (!parks.length) return;
  const inPark = (x: number, z: number) =>
    parks.some(
      ({ area, bb }) =>
        x >= bb.minX && x <= bb.maxX && z >= bb.minZ && z <= bb.maxZ && edgeDistance(area, x, z) >= 0,
    );
  const first = out.length;
  for (const road of world.roads) {
    if (out.length >= maxLamps) break;
    if (ROAD_SPECS[road.cls].drivable || road.deck) continue;
    const line = resample(road.pts, PARK_LAMP_SPACING);
    let side = hashUnit(world.seed, road.id ^ 0x2c1b3c6d) < 0.5 ? 1 : -1;
    for (let i = 0; i < line.length / 2 && out.length < maxLamps; i++) {
      const x = line[i * 2];
      const z = line[i * 2 + 1];
      const a = Math.max(0, i - 1);
      const b = Math.min(line.length / 2 - 1, i + 1);
      const dx = line[b * 2] - line[a * 2];
      const dz = line[b * 2 + 1] - line[a * 2 + 1];
      const len = Math.hypot(dx, dz);
      if (len < 1e-3) continue;
      const nx = (-dz / len) * side;
      const nz = (dx / len) * side;
      side = -side;
      const px = x + nx * PARK_LAMP_OFFSET;
      const pz = z + nz * PARK_LAMP_OFFSET;
      if (!inPark(px, pz)) continue;
      // The park lanterns are only kept clear of each other and of the mapped lamps: a street
      // lamp at the park's edge lights the street side of it, not the path.
      if (lampWithin(out, px, pz, PARK_LAMP_CLEARANCE, 0, mapped)) continue;
      if (lampWithin(out, px, pz, PARK_LAMP_CLEARANCE, first)) continue;
      if (surface.sample(px, pz).onRoad) continue;
      if (buildings.resolveCircle(px, pz, 0.6)) continue;
      out.push({
        x: px,
        z: pz,
        y: terrain.heightAt(px, pz),
        // The bracket over the path.
        rotY: Math.atan2(nz, -nx),
        scale: PARK_LAMP_SCALE,
        tint: WHITE,
      });
    }
  }
}

/**
 * Traffic signals on the arms of the bigger junctions.
 *
 * Three streets meeting is a junction; two is a bend, and putting a signal on one is the
 * giveaway that this was generated. The width floor is the same idea from the other end — a
 * crossroads of two residential streets in an old town is give-way in real life, and lighting
 * every one of them turns Kraków into a ring road.
 *
 * One pole per arm, on the near right-hand kerb of the approach and facing back down it,
 * which is where a driver arriving along that arm actually looks.
 */
const SIGNAL_MIN_ARMS = 3;
const SIGNAL_MIN_WIDTH = 11;
/** Metres past the edge of the junction disc, so a pole never stands in the crossing. */
const SIGNAL_SETBACK = 1.2;
/** Clearance from the kerb line, matching the offset the lamps use. */
const SIGNAL_KERB_OFFSET = 1.1;

function placeSignals(
  nodes: JunctionNode[],
  surface: SurfaceIndex,
  buildings: BuildingIndex,
  out: Placement[],
  maxSignals: number,
  surfaceOf: (road: Road, x: number, z: number) => number,
): void {
  for (const node of nodes) {
    if (out.length >= maxSignals) break;
    if (node.arms.length < SIGNAL_MIN_ARMS) continue;
    if (node.maxWidth < SIGNAL_MIN_WIDTH) continue;

    for (const arm of node.arms) {
      if (out.length >= maxSignals) break;
      if (arm.width < SIGNAL_MIN_WIDTH) continue;
      // Right of the direction of travel *into* the junction, which is the left of the arm's
      // own outward vector.
      const nx = -arm.dz;
      const nz = arm.dx;
      const offset = arm.width / 2 + SIGNAL_KERB_OFFSET;
      const along = node.r + SIGNAL_SETBACK;
      const px = node.x + arm.dx * along + nx * offset;
      const pz = node.z + arm.dz * along + nz * offset;
      if (buildings.resolveCircle(px, pz, 0.5)) continue;
      // The same test the lamps have, for the same reason: the kerb offset is from this
      // arm's centreline, and on a roundabout or a dual carriageway another carriageway is
      // there. Leszno put 59 of 98 poles on one tour stop in the road, a cluster of them on
      // a roundabout's ring.
      if (surface.sample(px, pz).onRoad) continue;
      out.push({
        x: px,
        z: pz,
        y: surfaceOf(arm.road, px, pz),
        // Local +X is the face of the head; point it back down the arm at the traffic. Same
        // construction as the lamp arm above, which is why it looks like it has a sign error.
        rotY: Math.atan2(arm.dz, -arm.dx),
        scale: 1,
        tint: WHITE,
      });
    }
  }
}

const WHITE = new THREE.Color(1, 1, 1);

// ---------------------------------------------------------------------------
// geometry
// ---------------------------------------------------------------------------

const _matrix = new THREE.Matrix4();
const _quat = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _scale = new THREE.Vector3();
const _axis = new THREE.Vector3(0, 1, 0);

function toInstancedMesh(
  name: string,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  placements: Placement[],
  castShadow: boolean,
): THREE.InstancedMesh | null {
  if (placements.length === 0) return null;
  const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
  mesh.name = name;
  mesh.castShadow = castShadow;
  mesh.receiveShadow = false;
  for (let i = 0; i < placements.length; i++) {
    const p = placements[i];
    _pos.set(p.x, p.y, p.z);
    _quat.setFromAxisAngle(_axis, p.rotY);
    _scale.setScalar(p.scale);
    _matrix.compose(_pos, _quat, _scale);
    mesh.setMatrixAt(i, _matrix);
    mesh.setColorAt(i, p.tint);
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  // Without this the mesh is culled against one tree's bounding sphere at the origin.
  mesh.computeBoundingSphere();
  mesh.matrixAutoUpdate = false;
  return mesh;
}

function triangleCount(geometry: THREE.BufferGeometry): number {
  return geometry.getAttribute('position').count / 3;
}

/**
 * A broadleaf: a short trunk under one faceted blob. Thirty triangles, and the per-instance
 * rotation means no two next to each other present the same silhouette.
 */
function makeBroadleafGeometry(): THREE.BufferGeometry {
  const trunk = solid(new THREE.CylinderGeometry(0.15, 0.22, 2.8, 5, 1, true), 0.0, 1.4, 0x6b5238);
  const canopy = new THREE.IcosahedronGeometry(1, 0);
  canopy.scale(1.6, 1.85, 1.6);
  return assemble([trunk, solid(canopy, 0, 3.6, 0x4d7c3c)]);
}

/** A conifer: two stacked cones. Used for `wood` and a fifth of everything else. */
function makeConiferGeometry(): THREE.BufferGeometry {
  const trunk = solid(new THREE.CylinderGeometry(0.13, 0.18, 1.6, 4, 1, true), 0, 0.8, 0x5e4a33);
  const lower = solid(new THREE.ConeGeometry(1.5, 3.2, 6), 0, 2.6, 0x35633a);
  const upper = solid(new THREE.ConeGeometry(1.0, 2.6, 6), 0, 4.5, 0x3c6d40);
  return assemble([trunk, lower, upper]);
}

/** A column, a bracket arm along +X, and a lamp head on the end of it. */
function makeLampGeometry(): THREE.BufferGeometry {
  const pole = solid(new THREE.CylinderGeometry(0.08, 0.13, 7.4, 5, 1, true), 0, 3.7, 0x50555c);
  const arm = new THREE.CylinderGeometry(0.06, 0.06, 1.6, 4, 1, true);
  arm.rotateZ(Math.PI / 2);
  const head = new THREE.BoxGeometry(0.82, 0.22, 0.34);
  return assemble([
    pole,
    solid(arm, 0.8, 7.3, 0x50555c),
    solid(head, 1.5, 7.16, 0x2f343a),
  ]);
}

/**
 * A traffic signal: a column, a dark housing, and three lenses on the face of it.
 *
 * The lenses are single planes rather than boxes, which is what keeps this affordable. A box
 * per lens is 36 triangles against 6, and at the signal cap that is the difference between
 * 11,000 triangles for the whole city and 23,000 — real money in the Marais, which builds at
 * 89% of the budget before any of this existed. A lens is only ever seen from the front, so
 * the back of it does not need to be there.
 *
 * All three are drawn dark. A signal with one lens lit needs per-instance colour, and
 * `instanceColor` tints the entire instance — it would light the pole as well. Three unlit
 * lenses read correctly from a car, and a lit one is not worth a second material for.
 */
function makeSignalGeometry(): THREE.BufferGeometry {
  const pole = solid(new THREE.CylinderGeometry(0.07, 0.09, 3.0, 5, 1, true), 0, 1.5, 0x4a4f55);
  const housing = solid(new THREE.BoxGeometry(0.3, 0.92, 0.34), 0, 3.3, 0x2b2f34);
  const parts = [pole, housing];
  // Just proud of the housing's own +X face at x = 0.15, so a lens never z-fights with it.
  for (const [y, color] of [[3.58, 0x8c3a32], [3.3, 0x94742c], [3.02, 0x3a7f4a]] as const) {
    const lens = new THREE.PlaneGeometry(0.16, 0.16);
    lens.rotateY(Math.PI / 2);
    parts.push(solid(lens, 0.16, y, color));
  }
  return assemble(parts);
}

/**
 * Warm sodium-tungsten, shared by the pool and the glow so a lamp reads as one light source
 * rather than two differently-coloured effects stacked on top of each other. Same family as
 * the lit-window colours in `textures.ts` — this is the same city at the same hour.
 */
const LAMP_LIGHT_COLOR = new THREE.Color(1, 0.72, 0.4);

/** Segments in the pool's fan. A streetlamp's pool is not a shape anyone inspects closely
 *  enough to see the facets; eight was visibly polygonal end-on, twelve was not. */
const POOL_SEGMENTS = 12;
/** Radius of the pool, in metres — big enough to read as light spilling onto the pavement
 *  beside the lamp, smaller than `LAMP_SPACING` so neighbouring pools never touch. */
const POOL_RADIUS = 3;
/**
 * How far along local +X — the arm's own direction, which the placement code already points
 * back over the carriageway — the pool is centred. The lamp's own head sits at `x = 1.5` (see
 * `makeLampGeometry`); centring the pool there rather than under the pole is what makes it
 * read as light falling from the lamp rather than a puddle glued to the bottom of the post.
 */
const POOL_OFFSET_X = 1.5;
/**
 * Height above the ground the pool is drawn at.
 *
 * The lamp's own base sits at bare terrain height, but the road it lights does not: a
 * carriageway is drawn `0.14 m` above the terrain and a kerb's pavement top sits `0.16 m`
 * above that (`ROAD_BASE`-ish and `KERB_HEIGHT` in `worldMesh.ts`), so anything drawn lower
 * than about `0.3 m` is quietly buried under the opaque road mesh and depth-tested away —
 * which is exactly what the first version of this did: `0.02 m`, invisible on every road it
 * was meant to be lighting. `0.36 m` clears the pavement top with a little to spare and
 * still reads as lying on the ground rather than hovering over it.
 */
const POOL_Y = 0.36;
/** Fully lit opacity of the pool. Tuned against Kraków at night: much above this and the
 *  additive fan bleeds into a flat wash that reads as a texture rather than as light. */
const POOL_MAX_OPACITY = 0.55;

/**
 * A soft radial fan on the ground under a lamp: full colour at the centre, fading to nothing
 * at the rim. The gradient is the whole trick and it costs no texture — additive blending on
 * a vertex colour of exactly zero contributes exactly nothing, so interpolating from
 * `LAMP_LIGHT_COLOR` down to black across each wedge is a real falloff for the price of one
 * extra vertex colour per triangle, the same instancing `makeLampGeometry` already pays for.
 *
 * Winding matches `addDisc` in `worldMesh.ts` — clockwise in (x, z) faces +Y — but `side` is
 * set to `DoubleSide` on the material anyway: a lamp only needs looking at from above, but a
 * pool invisible from the one wrong angle is a worse bug than one extra triangle direction.
 */
function buildLampPools(
  lamps: Placement[],
  terrain: Heightfield,
): THREE.BufferGeometry | null {
  if (lamps.length === 0) return null;
  const positions = new Float32Array(lamps.length * POOL_SEGMENTS * 9);
  const colors = new Float32Array(lamps.length * POOL_SEGMENTS * 9);
  let p = 0;
  let c = 0;

  /** Local (x, z) on the lamp's own axes to world, matching `toInstancedMesh`'s transform. */
  const place = (lamp: Placement, lx: number, lz: number): [number, number] => {
    const cos = Math.cos(lamp.rotY);
    const sin = Math.sin(lamp.rotY);
    return [
      lamp.x + lamp.scale * (lx * cos + lz * sin),
      lamp.z + lamp.scale * (-lx * sin + lz * cos),
    ];
  };
  const vertex = (lamp: Placement, lx: number, lz: number, lit: boolean) => {
    const [wx, wz] = place(lamp, lx, lz);
    positions[p++] = wx;
    // The ground under *this vertex*, not under the lamp. That is the whole fix. Plus however
    // far the lamp's own street is carried above the ground, for a lamp on a bridge.
    const lifted = lamp.y - terrain.heightAt(lamp.x, lamp.z);
    positions[p++] = terrain.heightAt(wx, wz) + lifted + POOL_Y;
    positions[p++] = wz;
    colors[c++] = lit ? LAMP_LIGHT_COLOR.r : 0;
    colors[c++] = lit ? LAMP_LIGHT_COLOR.g : 0;
    colors[c++] = lit ? LAMP_LIGHT_COLOR.b : 0;
  };

  for (const lamp of lamps) {
    for (let i = 0; i < POOL_SEGMENTS; i++) {
      const a0 = (i / POOL_SEGMENTS) * Math.PI * 2;
      const a1 = ((i + 1) / POOL_SEGMENTS) * Math.PI * 2;
      vertex(lamp, POOL_OFFSET_X, 0, true);
      vertex(lamp, POOL_OFFSET_X + Math.cos(a1) * POOL_RADIUS, Math.sin(a1) * POOL_RADIUS, false);
      vertex(lamp, POOL_OFFSET_X + Math.cos(a0) * POOL_RADIUS, Math.sin(a0) * POOL_RADIUS, false);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.computeBoundingSphere();
  return geometry;
}

/** Radius of the glow blob at the head, in metres — a little short of the head box's own
 *  half-length (0.41 m) so it reads as light coming *from* the fitting, not a ball around it. */
const GLOW_RADIUS = 0.34;
/** Fully lit opacity of the head glow. Higher than the pool: it is the brightest single point
 *  in the whole night render and is what actually reads as "this is a light" from a distance
 *  the pool on the ground is too flat and too far below the eyeline to sell on its own. */
const GLOW_MAX_OPACITY = 0.9;
const GLOW_INTENSITY = 4;

/**
 * A small blob at the lamp head, in the same place `makeLampGeometry` puts the fitting.
 *
 * A real bulb glow is round from every direction, which a billboard is not without extra
 * per-frame work to face the camera — not worth it for hundreds of instances. A low-poly
 * sphere is round for free and costs twenty triangles, the same order as the pool.
 */
function makeLampGlowGeometry(): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(GLOW_RADIUS, 0);
  geometry.translate(1.5, 7.16, 0);
  geometry.deleteAttribute('normal');
  geometry.deleteAttribute('uv');
  return geometry;
}

/**
 * Moves a primitive into place, flattens it, and paints every vertex one colour.
 *
 * Non-indexed throughout: it is what `mergeGeometries` needs to combine parts, and it is
 * also what makes flat shading come out right without any vertex-splitting work.
 */
function solid(geometry: THREE.BufferGeometry, x: number, y: number, color: number): THREE.BufferGeometry {
  geometry.translate(x, y, 0);
  const flat = geometry.index ? geometry.toNonIndexed() : geometry;
  if (flat !== geometry) geometry.dispose();
  flat.deleteAttribute('uv');
  const count = flat.getAttribute('position').count;
  const c = new THREE.Color().setHex(color, THREE.SRGBColorSpace);
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  flat.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return flat;
}

function assemble(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const merged = mergeGeometries(parts, false);
  for (const part of parts) part.dispose();
  if (!merged) throw new Error('prop geometry parts did not merge');
  merged.computeBoundingSphere();
  return merged;
}
